import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import {
  ConfirmationModal,
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Input,
  LinkButtonTo,
  ListItem,
  Radio,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  useCalendarHolidayDateSave,
  useCalendarHolidayDatesDelete,
} from "@/queries/use-calendar-holidays.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiCalendarHolidayDate,
  ApiCalendarHolidayDateSavePayload,
  ApiCalendarHolidayDateType,
} from "@/types/calendar-holiday.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface CalendarHolidayEditDateModalProps extends ModalProps {
  calendarHolidayId: number;
  calendarHolidayDate: ApiCalendarHolidayDate;
}

export const CalendarHolidayEditDateModal = ({
  calendarHolidayId,
  calendarHolidayDate,
  ...props
}: CalendarHolidayEditDateModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const modalsService = useModals();

  const calendarHolidaySchema = z.object({
    title: z.string().min(1, t("errors:field_required")),
    date: z.date({ error: t("errors:field_required") }),
    type: z.enum(ApiCalendarHolidayDateType),
  });

  type CalendarHolidayDateSchema = z.infer<typeof calendarHolidaySchema>;
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CalendarHolidayDateSchema>({
    mode: "onSubmit",
    resolver: zodResolver(calendarHolidaySchema),
    values: {
      title: calendarHolidayDate.title,
      date: calendarHolidayDate.date,
      type: calendarHolidayDate.type,
    },
  });
  const calendarHolidayDateSave = useCalendarHolidayDateSave(control);
  const calendarHolidayDateDelete = useCalendarHolidayDatesDelete();

  const onSubmit = handleSubmit(async (schema) => {
    const savePayload: ApiCalendarHolidayDateSavePayload = {
      title: schema.title,
      date: schema.date,
      type: schema.type,
    };
    calendarHolidayDateSave.mutate(
      { calendarHolidayId: calendarHolidayId, id: calendarHolidayDate.id, payload: savePayload },
      {
        onSuccess: (data) => {
          if (calendarHolidayDate.id == 0) {
            analyticsService.trackEvent(analyticEvents.calendarHolidays.holidayCreated, {
              [analyticProperties.id]: data.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.calendarHolidays.holidayEdited, {
              [analyticProperties.id]: data.id,
            });
          }
          close(id);
        },
      }
    );
  });

  const handleDelete = useCallback(async () => {
    if (!calendarHolidayDate) return;

    modalsService.openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await calendarHolidayDateDelete.mutateAsync({
            id: calendarHolidayDate.id,
            calendarHolidayId: calendarHolidayId,
          });
          analyticsService.trackEvent(analyticEvents.calendarHolidays.holidayDeleted, {
            [analyticProperties.id]: calendarHolidayDate.id,
          });
          close(id);
        } catch {
          return;
        }
      },
    });
  }, [calendarHolidayDate]);

  return (
    <>
      <Modal
        {...props}
        title={t("core:calendar_holiday_edit_title")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={calendarHolidayDateSave.isPending}
              onClick={onSubmit}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack>
            <Input
              label={t("common:title")}
              required={true}
              autoFocus={true}
              type="text"
              {...register("title")}
              error={errors.title?.message}
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Datepicker
                  value={value}
                  onChange={onChange}
                  required={true}
                  label={t("common:date")}
                  error={errors.date?.message}
                />
              )}
              control={control}
              name={"date"}
            />
            <Stack>
              <Title
                header={t("core:calendar_holiday_date_type")}
                size={"xs"}
                paddingBottom
                paddingTop
              />
              <Box variant={"border"}>
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <>
                      <Radio
                        value={ApiCalendarHolidayDateType.non_working}
                        checked={value == ApiCalendarHolidayDateType.non_working}
                        title={t("core:calendar_holiday_date_non_working_title")}
                        caption={t("core:calendar_holiday_date_non_working_description")}
                        onChange={() => {
                          onChange(ApiCalendarHolidayDateType.non_working);
                        }}
                      />
                      <Radio
                        value={ApiCalendarHolidayDateType.compensated}
                        checked={value == ApiCalendarHolidayDateType.compensated}
                        title={t("core:calendar_holiday_date_compensated_title")}
                        caption={t("core:calendar_holiday_date_compensated_description")}
                        onChange={() => {
                          onChange(ApiCalendarHolidayDateType.compensated);
                        }}
                      />
                      <Radio
                        value={ApiCalendarHolidayDateType.working}
                        checked={value == ApiCalendarHolidayDateType.working}
                        title={t("core:calendar_holiday_date_working_title")}
                        caption={t("core:calendar_holiday_date_working_description")}
                        onChange={() => {
                          onChange(ApiCalendarHolidayDateType.working);
                        }}
                      />
                    </>
                  )}
                  control={control}
                  name={"type"}
                />
              </Box>
            </Stack>
          </Stack>
          {calendarHolidayDate.id > 0 && (
            <Stack>
              <hr className="border-dark/10" />
              <ListItem
                valueSlot={
                  <LinkButtonTo onClick={handleDelete} variant={"danger"}>
                    {t("common:delete")}
                  </LinkButtonTo>
                }
              ></ListItem>
            </Stack>
          )}
        </form>
      </Modal>
    </>
  );
};
